import React, { PropsWithChildren, useEffect } from "react";
import {
  AlertDialogAction,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogContent,
  AlertDialogTrigger,
  AlertDialog,
} from "@/components/ui";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { STOP_BULK_MESSAGE_MUTATION } from "@/api/mutations/stopBulkMessage";
import { toast } from "sonner";
import { BULK_MESSAGES_QUERY } from "@/api/queries/bulkMessages";

export function StopButton({
  id,
  onSuccess,
  children,
}: PropsWithChildren<{
  id?: string;
  onSuccess?: () => void;
}>) {
  const { t } = useTranslation();

  const [stopBulkMessageMutation, { data }] = useMutation(
    STOP_BULK_MESSAGE_MUTATION,
    { refetchQueries: [BULK_MESSAGES_QUERY] },
  );

  useEffect(() => {
    if (data?.stopBulkMessageMutation?.success) {
      toast.success(t("bulkMessaging.table.stop.success"));
      if (onSuccess) {
        onSuccess();
      }
    } else if (data?.stopBulkMessageMutation?.errors?.length) {
      const { errors } = data?.stopBulkMessageMutation ?? {};
      toast.error(t("bulkMessaging.table.stop.error"), {
        description: errors?.map(({ message }) => message).join(", "),
      });
    }
  }, [data, onSuccess, t]);

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("bulkMessaging.table.stop.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("bulkMessaging.table.stop.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t("bulkMessaging.table.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() =>
              id &&
              stopBulkMessageMutation({
                variables: { bulkMessageId: id },
              })
            }
          >
            {t("bulkMessaging.table.yes")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
